/**
 * Minimal error boundary — keeps a child component crash from
 * taking down the whole stage plot editor. Shows the error
 * message instead of a blank screen.
 */
import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  label?: string;
}
interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.label, error);
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            margin: 16,
            padding: 16,
            border: '1px solid #e74c3c',
            borderRadius: 8,
            color: '#c0392b',
            fontFamily: 'monospace',
            fontSize: 'var(--font-sm)',
            whiteSpace: 'pre-wrap',
          }}
        >
          <strong>{this.props.label ?? 'Error'}</strong>
          {'\n'}
          {this.state.error.message}
          {'\n'}
          {this.state.error.stack?.split('\n').slice(0, 4).join('\n')}
        </div>
      );
    }
    return this.props.children;
  }
}
